import type { ConflitoDetectado, ConflitosSnapshotResponse, GanttSnapshotData, GanttTask } from '@pieltec/contracts';

function objeto(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
const numero = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);
const textos = (value: Record<string, unknown>, keys: readonly string[]) => keys.every(key => typeof value[key] === 'string');

function tarefa(value: unknown): value is GanttTask {
  if (!objeto(value) || !textos(value, ['id', 'text', 'start_date']) || !numero(value.duration)) return false;
  return ['parent', 'celulaId', 'status'].every(key => value[key] === undefined || typeof value[key] === 'string')
    && (value.type === undefined || value.type === 'project' || value.type === 'task')
    && ['open', 'conflito'].every(key => value[key] === undefined || typeof value[key] === 'boolean')
    && (value.duracaoHoras === undefined || numero(value.duracaoHoras))
    && (value.posicaoFila === undefined || value.posicaoFila === null || numero(value.posicaoFila));
}

const tipos = ['sobrecarga', 'precedencia', 'dupla_alocacao', 'prazo', 'movimento_concorrente'] as const;
function conflito(value: unknown): value is ConflitoDetectado {
  if (!objeto(value) || !textos(value, ['id', 'mensagem']) || !tipos.some(tipo => tipo === value.tipo)) return false;
  return ['titulo', 'descricao', 'opId', 'opNumero', 'osId', 'celulaId', 'recursoId', 'dataInicio', 'dataFim', 'dataOcorrencia']
    .every(key => value[key] === undefined || typeof value[key] === 'string')
    && ['horasPlanejadas', 'horasDisponiveis'].every(key => value[key] === undefined || numero(value[key]))
    && (value.severidade === undefined || value.severidade === 'critico' || value.severidade === 'alerta' || value.severidade === 'info');
}

function celula(value: unknown): boolean {
  if (!objeto(value) || !textos(value, ['codigo', 'nome']) || !objeto(value.capacidade)) return false;
  const capacidade = value.capacidade;
  return textos(capacidade, ['celulaId', 'celulaCodigo', 'celulaNome'])
    && ['horasDisponiveis', 'horasPlanejadas', 'percentualOcupacao'].every(key => numero(capacidade[key]))
    && ['normal', 'proxima-limite', 'sobrecarregada'].some(estado => estado === capacidade.estado);
}

export function ehSnapshotGantt(value: unknown): value is GanttSnapshotData {
  return objeto(value) && Array.isArray(value.data) && value.data.every(tarefa)
    && Array.isArray(value.links) && value.links.every(link => objeto(link) && textos(link, ['id', 'source', 'target', 'type']))
    && Array.isArray(value.celulas) && value.celulas.every(celula)
    && Array.isArray(value.conflitos) && value.conflitos.every(conflito);
}

export function ehSnapshotConflitos(value: unknown): value is ConflitosSnapshotResponse {
  if (!objeto(value) || !numero(value.total) || !Array.isArray(value.conflitos) || !value.conflitos.every(conflito)
    || value.total !== value.conflitos.length || !objeto(value.agrupadoPorTipo)) return false;
  const grupos = value.agrupadoPorTipo;
  const agrupados: ConflitoDetectado[] = [];
  for (const tipo of tipos) {
    const grupo = grupos[tipo];
    if (!Array.isArray(grupo)) return false;
    for (const item of grupo) {
      if (!conflito(item) || item.tipo !== tipo) return false;
      agrupados.push(item);
    }
  }
  const identidade = (item: ConflitoDetectado) => JSON.stringify([item.id, item.tipo]);
  const ids = new Set(value.conflitos.map(identidade));
  const agrupadosIds = new Set(agrupados.map(identidade));
  return ids.size === value.conflitos.length && agrupados.length === value.conflitos.length
    && agrupadosIds.size === agrupados.length && agrupados.every(item => ids.has(identidade(item)));
}
